import {
  DayOfWeek,
  MetricField,
  MetricUnit,
  ReportScheduleRecurrence as Recurrence,
} from '@/__generated__/graphql';
import { DayjsSchema } from '@/utils/zod';
import { t } from '@lingui/macro';
import z from 'zod/v4';

const MetricSchema = z.object({
  name: z.enum(MetricField),
  unit: z.enum(MetricUnit),
});

const ScheduleSchema = z
  .object({
    recurrence: z.enum(Recurrence),
    time: DayjsSchema,
    dayOfWeek: z.enum(DayOfWeek).nullish(),
    daysOfWeek: z.array(z.enum(DayOfWeek)).nullish(),
  })
  .refine((data) => data.recurrence !== Recurrence.Weekly || data.daysOfWeek?.length, {
    message: t`Debes seleccionar al menos un día de la semana.`,
    path: ['daysOfWeek'],
  })
  .refine(
    (data) =>
      ![Recurrence.MonthlyFirstDay, Recurrence.MonthlyLastDay].includes(data.recurrence) ||
      data.dayOfWeek != null,
    {
      message: t`Debes seleccionar un día de la semana.`,
      path: ['dayOfWeek'],
    },
  );

const EmailSchema = z.object({
  email: z.email(),
});

const Step1Schema = z.object({
  devices: z.array(z.string().min(1)).min(1),
  metrics: z.array(MetricSchema).min(1),
});

const Step2Schema = z.object({
  period: z.object({
    start: z.string(),
    stop: z.string().nullable(),
  }),
  meanWindow: z.string().nullable(),
  timezone: z.string(),
});

const Step3Schema = z
  .object({
    createPdf: z.boolean(),
    createExcel: z.boolean(),
    emails: z.array(EmailSchema).min(1),
  })
  .check(({ value, issues }) => {
    if (!value.createExcel && !value.createPdf) {
      issues.push({
        code: 'custom',
        message: t`Debes seleccionar al menos un formato`,
        input: value.createPdf,
        path: ['createPdf'],
      });

      issues.push({
        code: 'custom',
        message: t`Debes seleccionar al menos un formato`,
        input: value.createExcel,
        path: ['createExcel'],
      });
    }
  });

const Step4Schema = z
  .object({
    enabled: z.boolean(),
    schedule: ScheduleSchema.optional(),
    saveTemplate: z.boolean(),
    templateName: z.string().optional(),
    templateDescription: z.string().optional(),
  })
  .refine((data) => !data.saveTemplate || data.templateName?.length, {
    error: t`Debes proveer un nombre para guardar el reporte`,
    path: ['templateName'],
  });

export const CreateReportSchema = z.object({
  templateId: z.int().optional(),
  step1: Step1Schema,
  step2: Step2Schema,
  step3: Step3Schema,
  step4: Step4Schema,
});

export type CreateReportFieldValues = z.output<typeof CreateReportSchema>;
